import { Check } from 'lucide-react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { localClock } from '../../shared/lib/datetime';

/**
 * 下注成功回执。视觉规格 §10.3 的升级版（2026-08-19 评审：toast 太小，要弹窗）。
 *
 * <p><b>勾是青色，不是金色也不是绿色。</b> D4 里金 = 钱/赢、绿 = 赢/到账，
 * 而这里只是买了一张票 —— 借用中奖的配色，玩家会有半秒钟以为自己赢了，
 * 那半秒的落差比没有仪式感更伤。青是「活信号 / 已确认」，正好。
 *
 * <p>同理**没有金币雨**：D4 §0.5 的情绪白名单只批给中奖，买票不在其中。
 * 仪式感来自留白、字号层级和那一下弹入。
 *
 * <p>说的那两句好话也要守住边界：可以祝好运（巴西彩票语境里 `boa sorte` 是日常
 * 用语），但**不能暗示会赢**。
 */
export function BetConfirmSheet({
  pick,
  rounds,
  quantity,
  totalCents,
  currency,
  roundNumber,
  roundLabel,
  drawAt,
  onSeeBets,
  onAgain,
  onClose,
}: {
  pick: string;
  rounds: number;
  quantity: number;
  totalCents: number;
  currency: string;
  roundNumber: number;
  /** 日内序号（`#01` / `#001`）。缺席时退回全局期号，而不是留白。 */
  roundLabel?: string;
  /** 这一期几点开奖。按设备时区渲染 —— 玩家刚下完注，最想知道的就是「什么时候出」。 */
  drawAt?: number;
  onSeeBets: () => void;
  onAgain: () => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="profile-sheet milhar-confirm-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('milharBetSheetTitle')}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-grabber" />
        <div className="milhar-confirm-body">
          <span className="milhar-confirm-check" aria-hidden="true"><Check size={26} /></span>
          <h2 className="milhar-confirm-title">{t('milharBetSheetTitle')}</h2>
          <p className="milhar-confirm-luck">{t('milharBetSheetLuck')}</p>

          <div className="milhar-confirm-pick mono">
            {pick.split('').map((c, i) => <span key={i}>{c}</span>)}
          </div>

          <dl className="milhar-confirm-rows">
            <div>
              <dt>{t('milharRoundIdLabel')}</dt>
              {/* 日内序号，不是五位数全局号 —— 这张弹层是玩家记住「我押的是哪一期」
                  的唯一时机。全局号留在 title 里给客服。 */}
              <dd className="mono" title={`global #${roundNumber}`}>
                {roundLabel || `#${roundNumber}`}
              </dd>
            </div>
            {drawAt ? (
              <div>
                <dt>{t('milharHistTime')}</dt>
                {/* 期号说「哪一期」，时刻说「什么时候出」。只给期号的话，玩家还得
                    退出去自己算 —— 而这正是他刚付完钱最在意的一件事。 */}
                <dd className="mono">{localClock(drawAt)}</dd>
              </div>
            ) : null}
            {rounds > 1 || quantity > 1 ? (
              <div>
                <dt>{t('milharMoreOptions')}</dt>
                <dd className="mono">
                  {`${rounds} ${t(rounds === 1 ? 'milharUnitRound' : 'milharUnitRounds')} · ${quantity} ${t(quantity === 1 ? 'milharUnitBet' : 'milharUnitBets')}`}
                </dd>
              </div>
            ) : null}
            <div className="is-total">
              <dt>{t('milharBetSheetDebited')}</dt>
              <dd className="mono">{fmtCurrency(totalCents / 100, currency)}</dd>
            </div>
          </dl>

          <p className="milhar-confirm-note">{t('milharBetSheetPublic')}</p>

          <div className="milhar-confirm-acts">
            <button type="button" className="milhar-confirm-primary tap" onClick={onSeeBets}>
              {t('milharBetSheetSeeBets')}
            </button>
            <button type="button" className="milhar-confirm-ghost tap" onClick={onAgain}>
              {t('milharBetSheetAgain')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
